import { useQuery } from 'react-query';
import { Session } from '@utils/types/editors/sessions.ts';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';

async function getSession(id?: string): Promise<BaseResponse<Session>> {
  const { data } = await Axios.get(GET_URL(id).session);
  return data;
}

const POLLING_INTERVAL_MS = 30_000;
// Regeneration jobs are usually seconds, not minutes — poll fast enough
// that the editor stops seeing the pending state within a beat of the
// worker finishing.
const REGEN_POLLING_INTERVAL_MS = 3_000;

function isWarming(session?: Session): boolean {
  if (!session) return false;
  const tier = session.access_tier ?? 'hot';
  if (tier === 'cold') return true;
  if (tier === 'archive' && !!session.archive_status) return true;
  return false;
}

function isWaveInfoJobInFlight(session?: Session): boolean {
  const status = session?.wave_info_status;
  return status === 'pending' || status === 'running';
}

export function useGetTranscriptSession(id?: string) {
  return useQuery<BaseResponse<Session>, AxiosError<BaseError>>(
    ['transcript-editor-session', id],
    () => getSession(id),
    {
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: data => {
        // Regen state changes fast; a warming tier changes on the order
        // of minutes. Pick the tighter interval when both apply.
        if (isWaveInfoJobInFlight(data?.result))
          return REGEN_POLLING_INTERVAL_MS;
        if (isWarming(data?.result)) return POLLING_INTERVAL_MS;
        return false;
      }
    }
  );
}
